import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, delay },
});

const Section = ({ number, title, children, delay = 0 }) => (
  <motion.div {...fade(delay)} className="mb-12">
    <h2 className="font-display text-xl sm:text-2xl font-bold mb-4" style={{ color: "#1A1A1A" }}>
      {number}. {title}
    </h2>
    <div className="space-y-3 text-base leading-relaxed" style={{ color: "rgba(26,26,26,0.6)" }}>
      {children}
    </div>
  </motion.div>
);

export default function TermsOfUse() {
  return (
    <div className="min-h-screen relative overflow-hidden" style={{ backgroundColor: "#F5F5F0" }} data-testid="terms-page">
      {/* ── HEADER ── */}
      <header
        className="fixed top-0 left-0 right-0 z-50 border-b"
        style={{ backgroundColor: "rgba(245,245,240,0.85)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", borderColor: "rgba(0,0,0,0.06)" }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="terms-logo-link">
            <img
              src="https://customer-assets.emergentagent.com/job_2d4aff0d-e5b7-449b-860f-09baa683a8bc/artifacts/b3uq1zlx_Logo.png"
              alt="Prysh AI"
              className="w-9 h-9 object-contain"
            />
            <span className="font-display text-lg font-semibold" style={{ color: "#1A1A1A" }}>Prysh AI</span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium transition-colors"
            style={{ color: "rgba(26,26,26,0.5)" }}
            data-testid="terms-back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
        </div>
      </header>

      {/* ── HERO ── */}
      <section className="pt-32 pb-12 px-6 md:px-12">
        <div className="max-w-3xl mx-auto text-center">
          <motion.h1
            {...fade(0.1)}
            className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05]"
            style={{ color: "#FF5A36" }}
            data-testid="terms-headline"
          >
            Terms of Use
          </motion.h1>
          <motion.p {...fade(0.2)} className="mt-4 text-sm" style={{ color: "rgba(26,26,26,0.4)" }}>
            Last Updated: 1 April 2026
          </motion.p>
        </div>
      </section>

      {/* ── INTRO ── */}
      <section className="px-6 md:px-12 pb-8">
        <motion.div {...fade()} className="max-w-3xl mx-auto">
          <p className="text-base leading-relaxed" style={{ color: "rgba(26,26,26,0.6)" }}>
            Welcome to Prysh AI Labs ("Prysh", "we", "our", or "us"). These Terms of Use ("Terms") govern your access to and use of our AI-powered video creation platform.
          </p>
          <p className="text-base leading-relaxed mt-3" style={{ color: "rgba(26,26,26,0.6)" }}>
            By accessing or using Prysh, you agree to these Terms. If you do not agree, please do not use the platform.
          </p>
        </motion.div>
      </section>

      {/* ── SECTIONS ── */}
      <section className="px-6 md:px-12 pb-20">
        <div className="max-w-3xl mx-auto">

          <Section number={1} title="Our Service">
            <p>Prysh AI Labs provides an AI-powered platform that enables users to create video advertisements and marketing content using automated tools.</p>
            <p>We may update, modify, or discontinue features at any time without prior notice.</p>
          </Section>

          <Section number={2} title="User Accounts">
            <ul className="list-disc pl-6 space-y-1.5">
              <li>You must provide accurate and complete information when creating an account</li>
              <li>You are responsible for maintaining the confidentiality of your login credentials</li>
              <li>You are responsible for all activity under your account</li>
            </ul>
            <p>We reserve the right to suspend or terminate accounts that violate these Terms.</p>
          </Section>

          <Section number={3} title="Acceptable Use">
            <p>You agree not to use Prysh to create, upload, or distribute content that:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Is illegal, fraudulent, or misleading</li>
              <li>Contains explicit, pornographic, or adult material</li>
              <li>Promotes hate speech, violence, or harassment</li>
              <li>Infringes intellectual property rights (copyright, trademarks, etc.)</li>
              <li>Impersonates any person, brand, or entity</li>
              <li>Creates deceptive or harmful deepfakes</li>
            </ul>
            <p>You are solely responsible for the content you generate using Prysh.</p>
          </Section>

          <Section number={4} title="User Inputs & Rights">
            <p>You represent and warrant that:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>You own or have the necessary rights to all content (text, images, logos, etc.) you upload</li>
              <li>Your inputs do not violate any laws or third-party rights</li>
            </ul>
            <p>We are not responsible for verifying ownership of user-provided content.</p>
          </Section>

          <Section number={5} title="AI-Generated Content Disclaimer">
            <p>Prysh uses artificial intelligence to generate content.</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Outputs may not always be accurate, unique, or error-free</li>
              <li>We do not guarantee ad performance, engagement, or business outcomes</li>
              <li>You are responsible for reviewing and approving content before use</li>
            </ul>
            <p>Prysh acts solely as a tool to assist in content creation.</p>
          </Section>

          <Section number={6} title="Intellectual Property">
            <ul className="list-disc pl-6 space-y-1.5">
              <li>You retain ownership of the final content generated using our platform, subject to these Terms</li>
              <li>Prysh retains all rights to its technology, models, platform, and software</li>
            </ul>
            <p>We may use anonymized and aggregated data to improve our services.</p>
          </Section>

          <Section number={7} title="Payments & Usage">
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Certain features may require payment or subscription</li>
              <li>Pricing, usage limits, and credits will be defined on the platform</li>
              <li>All payments are non-refundable unless otherwise stated</li>
            </ul>
          </Section>

          <Section number={8} title="Third-Party Services">
            <p>Our platform may rely on third-party services and APIs.</p>
            <p>We are not responsible for:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Downtime or errors caused by third-party providers</li>
              <li>Any loss resulting from such disruptions</li>
            </ul>
          </Section>

          <Section number={9} title="Limitation of Liability">
            <p>To the maximum extent permitted by law:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Prysh shall not be liable for any indirect, incidental, or consequential damages</li>
              <li>This includes loss of revenue, profits, or business opportunities</li>
              <li>Our total liability shall not exceed the amount paid by you in the past 3 months</li>
            </ul>
          </Section>

          <Section number={10} title="Termination">
            <p>We may suspend or terminate your access:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>If you violate these Terms</li>
              <li>If required by law</li>
              <li>For misuse of the platform</li>
            </ul>
            <p>You may stop using the platform at any time.</p>
          </Section>

          <Section number={11} title="Privacy">
            <p>Your use of Prysh is also governed by our Privacy Policy.</p>
          </Section>

          <Section number={12} title="Changes to Terms">
            <p>We may update these Terms from time to time.</p>
            <p>Continued use of the platform after changes constitutes acceptance of the updated Terms.</p>
          </Section>

          <Section number={13} title="Governing Law">
            <p>These Terms shall be governed by the laws of India.</p>
            <p>Any disputes shall be subject to the jurisdiction of courts in Delhi / Gurgaon, India.</p>
          </Section>

        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="relative z-10 border-t py-12 px-6 md:px-12" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-sm mb-4" style={{ color: "rgba(26,26,26,0.5)" }}>
            For questions, contact us at:{" "}
            <a href="mailto:info@pryshai.com" className="font-medium hover:underline" style={{ color: "#FF5A36" }} data-testid="terms-contact-email">
              info@pryshai.com
            </a>
          </p>
          <p className="text-xs" style={{ color: "rgba(26,26,26,0.25)" }}>
            &copy; {new Date().getFullYear()} Prysh AI Labs. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
